import { faExclamationTriangle, faUpload } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Flex, Text } from '@modusign/design-system';
import { useRef } from 'react';

import { WindowModal, WindowModalHeader } from '@app/components/Modal/WindowModal';

import {
  translateCellType,
  translateExcelValidationError,
} from '../../utils/excelValidationErrorMapping';

interface BatchUpdateErrorModalProps {
  errors: Array<{ cell: string; error: string; type: string }>;
  isOpen: boolean;
  onClose: () => void;
  onSetFile: (file: File) => void;
}

export default function BatchUpdateErrorModal({
  errors,
  isOpen,
  onClose,
  onSetFile,
}: BatchUpdateErrorModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <WindowModal isOpen={isOpen} maxWidth="xmd" onRequestClose={onClose}>
      <WindowModalHeader onClose={onClose}>
        <FontAwesomeIcon icon={faUpload} style={{ marginRight: '4px' }} />
        엑셀 업로드
      </WindowModalHeader>
      <Flex height="480px" width="100%">
        <input
          accept=".xlsx"
          hidden
          ref={fileInputRef}
          type="file"
          onChange={(e) => {
            if (e.target.files?.[0]) {
              onSetFile(e.target.files?.[0]);
            }
          }}
        />
        <Flex
          borderRight="1px solid"
          borderRightColor="lightGray300"
          flexDirection="column"
          p={7}
          width="255px"
        >
          <Box as="span" color="iconNegativeBase" fontSize="28px">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </Box>
          <Text fontWeight="bold" mt={5}>
            데이터에 오류가 있습니다.
          </Text>
          <Text as="p" mt={3}>
            우측의 오류들을 확인해 주세요.
            <br />
            오류를 해결한 뒤 엑셀파일을
            <br />재 업로드해 주세요.
          </Text>
          <Button mt="auto" width="100%" onClick={() => fileInputRef.current?.click()}>
            엑셀 재 업로드
          </Button>
        </Flex>
        <Flex
          as="ul"
          bg="lightGray100"
          flex="auto"
          flexDirection="column"
          gap={4}
          overflow="auto"
          p={6}
        >
          {errors.map(({ cell, error, type }) => (
            <ErrorListItem
              columnName={cell}
              key={type}
              message={translateExcelValidationError(error)}
              type={translateCellType(type)}
            />
          ))}
        </Flex>
      </Flex>
    </WindowModal>
  );
}

interface ErrorListItemProps {
  columnName: string;
  type: string;
  message: string;
}

function ErrorListItem({ columnName, message, type }: ErrorListItemProps) {
  return (
    <Box
      as="li"
      bg="white"
      borderColor="boxBorderNeutralBase"
      borderRadius="4px"
      boxShadow="0px 1px 4px rgba(0, 0, 0, 0.0588235)"
      px={5}
      py={4}
    >
      <Box>
        <Text color="textNegativeDark" fontWeight="bold">
          {columnName}
        </Text>
        <Text fontWeight="bold" ml={1}>
          {type}
        </Text>
      </Box>
      <Text as="p" mt="2px">
        {message}
      </Text>
    </Box>
  );
}
